/**
 * ArtifactListPanelContent
 *
 * Shared shell for the map side-panel artifact lists (tasks / notes /
 * obstructions). Owns everything those three lists have in common — the
 * `SearchAndFilters` header (search bar + filter toggle + close button), the
 * filter-expansion state, the count header with an "Add" button, and the list
 * container — so each artifact only supplies its store-wired slots (search
 * binding, filter pills + panel, count/add metadata) and its list as children.
 *
 * The close affordance mirrors the public shared map on mobile: a collapse
 * icon (full-screen list slides away) instead of a bare "X".
 *
 * @module components/ui/SidePanel/ArtifactListPanelContent
 */
import { memo, useCallback, useState, type ReactNode } from 'react';
import { ActionIcon, Button } from '@mantine/core';
import { IconLayoutSidebarLeftCollapse, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { SearchAndFilters } from '@/components/ui/SearchAndFilters';
import { ICON_SIZE_LG, ICON_SIZE_SM } from '@/constants/ui';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';

import styles from './SidePanel.module.css';

interface ArtifactListPanelContentProps {
  readonly onClose: () => void;
  readonly search: {
    readonly placeholder: string;
    readonly value: string;
    readonly onChange: (value: string) => void;
  };
  readonly filters: {
    readonly hasActiveFilters: boolean;
    /** Tooltip + aria-label for the filter toggle. */
    readonly label: string;
    readonly pills: ReactNode;
    readonly panel: ReactNode;
  };
  readonly header: {
    readonly title: string;
    readonly count: number;
    readonly addLabel: string;
    readonly onAdd: () => void;
    readonly addDisabled?: boolean;
  };
  /** The artifact's list (TasksPanelList / PaginatedList). */
  readonly children: ReactNode;
}

export const ArtifactListPanelContent = memo(function ArtifactListPanelContent({
  onClose,
  search,
  filters,
  header,
  children,
}: ArtifactListPanelContentProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const handleToggleFilters = useCallback(() => {
    setFiltersExpanded((previous) => !previous);
  }, []);

  return (
    <>
      <SearchAndFilters>
        <SearchAndFilters.Header>
          <SearchAndFilters.SearchBar
            placeholder={search.placeholder}
            value={search.value}
            onChange={search.onChange}
          />

          <SearchAndFilters.FilterToggle
            expanded={filtersExpanded}
            hasActiveFilters={filters.hasActiveFilters}
            onClick={handleToggleFilters}
            tooltip={filters.label}
            aria-label={filters.label}
          />

          {/* Mobile only: collapse the full-screen list. Desktop closes via
              the side bar's toggle icon, so no close button is needed here. */}
          {isMobile && (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              onClick={onClose}
              aria-label={t('common.close')}
            >
              <IconLayoutSidebarLeftCollapse size={ICON_SIZE_LG} />
            </ActionIcon>
          )}
        </SearchAndFilters.Header>

        <SearchAndFilters.Section>
          <SearchAndFilters.Pills show={!filtersExpanded}>
            {filters.pills}
          </SearchAndFilters.Pills>

          <SearchAndFilters.FiltersPanel expanded={filtersExpanded}>
            {filters.panel}
          </SearchAndFilters.FiltersPanel>
        </SearchAndFilters.Section>
      </SearchAndFilters>

      <div className={styles.countHeader}>
        <span className={styles.countLabel}>{header.title}</span>
        <span className={styles.countBadge}>{header.count}</span>
        <Button
          size="xs"
          leftSection={<IconPlus size={ICON_SIZE_SM} />}
          onClick={header.onAdd}
          className={styles.addButton}
          disabled={header.addDisabled}
        >
          {header.addLabel}
        </Button>
      </div>

      <AppScrollArea fill>{children}</AppScrollArea>
    </>
  );
});
